import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { getMaterialCategories, createMaterialCategory } from '../../services/materials';
import { onQuerySuccess, onMutateSuccess } from '../../utils/queryResponse';
import { Form } from 'react-bootstrap';
import CreatableSelect from 'react-select/creatable';

const colorStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: 42,
        borderRadius: 10,
        borderColor: state.isFocused ? '#2563EB' : '#E9ECF3',
        boxShadow: state.isFocused ? '0 0 0 3px #EFF4FF' : '0 1px 2px rgba(16,24,40,.05)',
        fontSize: 13.5,
        backgroundColor: '#fff',
        transition: 'border-color .14s, box-shadow .14s',
        '&:hover': { borderColor: state.isFocused ? '#2563EB' : '#DCE7FF' },
    }),
    valueContainer: base => ({ ...base, padding: '2px 10px' }),
    placeholder: base => ({ ...base, fontSize: 13.5, color: '#9AA4B2', fontWeight: 500 }),
    singleValue: base => ({ ...base, color: '#0B1424', fontWeight: 600, fontSize: 13.5 }),
    input: base => ({ ...base, color: '#0B1424' }),
    indicatorSeparator: base => ({ ...base, backgroundColor: '#E9ECF3' }),
    dropdownIndicator: (base, state) => ({ ...base, color: state.isFocused ? '#2563EB' : '#9AA4B2', ':hover': { color: '#2563EB' } }),
    menu: base => ({ ...base, borderRadius: 12, overflow: 'hidden', border: '1px solid #E9ECF3', boxShadow: '0 12px 30px -8px rgba(16,24,40,.18)', zIndex: 40 }),
    menuPortal: base => ({ ...base, zIndex: 9999 }),
    option: (base, state) => ({
        ...base,
        fontSize: 13.5,
        fontWeight: 600,
        padding: '9px 12px',
        color: state.isSelected ? '#fff' : '#1C2536',
        backgroundColor: state.isSelected ? '#2563EB' : state.isFocused ? '#EFF4FF' : '#fff',
        cursor: 'pointer',
        ':active': { backgroundColor: state.isSelected ? '#2563EB' : '#DCE7FF' },
    }),
}

const MaterialCategorySelect = ({ value, onChange, required, ...rest }) => {
    const { t } = useTranslation();
    const [options, setOptions] = useState([])
    const queryClient = useQueryClient()

    const { data, isLoading } = useQuery(['material-categories'], () => getMaterialCategories())

    useEffect(() => {
        onQuerySuccess(data, (result) => {
            setOptions(result.map(d => ({ value: d.id, label: d.name })))
        })
    }, [data])

    const createMut = useMutation(createMaterialCategory, {
        onSuccess: onMutateSuccess((created) => {
            queryClient.invalidateQueries(['material-categories'])
            if (onChange) onChange({ value: created.id, label: created.name })
        })
    })

    const handleCreate = (inputValue) => {
        createMut.mutate({ name: inputValue })
    }

    return (
        <Form.Group>
            <div style={{ position: 'relative' }}>
                {required &&
                    <input
                        className="select-required"
                        type="text"
                        value={value ?? ''}
                        onChange={() => {}}
                        tabIndex={-1}
                        required={required}
                        style={{ zIndex: -1 }}
                    />
                }
                <CreatableSelect
                    styles={colorStyles}
                    value={options.find(opt => opt.value === value) || null}
                    options={options}
                    isLoading={isLoading || createMut.isLoading}
                    onChange={(selected) => onChange && onChange(selected)}
                    onCreateOption={handleCreate}
                    formatCreateLabel={(input) => `Create "${input}"`}
                    placeholder={t('materialCategorySelect.placeholder', 'Select or create...')}
                    isClearable
                    {...rest}
                />
            </div>
        </Form.Group>
    )
}

export default MaterialCategorySelect
